import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, TrendingUp, TrendingDown, DollarSign, 
  PieChart, BarChart3, Calendar, Save, RefreshCw, ArrowUpRight, 
  ArrowDownRight, Minus, IndianRupee, ChevronRight, ChevronDown, Eye, X
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FISCAL_MONTHS = [
  { month: 1, name: 'Apr', fullName: 'April' },
  { month: 2, name: 'May', fullName: 'May' },
  { month: 3, name: 'Jun', fullName: 'June' },
  { month: 4, name: 'Jul', fullName: 'July' },
  { month: 5, name: 'Aug', fullName: 'August' },
  { month: 6, name: 'Sep', fullName: 'September' },
  { month: 7, name: 'Oct', fullName: 'October' },
  { month: 8, name: 'Nov', fullName: 'November' },
  { month: 9, name: 'Dec', fullName: 'December' },
  { month: 10, name: 'Jan', fullName: 'January' },
  { month: 11, name: 'Feb', fullName: 'February' },
  { month: 12, name: 'Mar', fullName: 'March' }
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  
  if (Math.abs(num) >= 10000000) {
    return `₹${(num / 10000000).toFixed(2)}Cr`;
  } else if (Math.abs(num) >= 100000) {
    return `₹${(num / 100000).toFixed(2)}L`;
  } else if (Math.abs(num) >= 1000) {
    return `₹${(num / 1000).toFixed(2)}K`;
  }
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatFullCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const PnLPortal = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [yoyData, setYoyData] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [selectedCostCategory, setSelectedCostCategory] = useState('');
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showNewFYDialog, setShowNewFYDialog] = useState(false);
  const [showMonthDetailDialog, setShowMonthDetailDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedMonthDetail, setSelectedMonthDetail] = useState(null);
  const [monthDetailLoading, setMonthDetailLoading] = useState(false);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', type: 'cost', description: '' });
  const [entryForm, setEntryForm] = useState({ category_id: '', fiscal_year: '', entries: {} });
  const [newFYForm, setNewFYForm] = useState({ fiscal_year: '' });
  
  // Expanded categories for data entry
  const [expandedCategories, setExpandedCategories] = useState({});

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const fetchFiscalYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pnl/fiscal-years`, config);
      setFiscalYears(response.data.fiscal_years);
      if (!selectedFiscalYear) {
        setSelectedFiscalYear(response.data.current);
      }
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
    }
  }, [selectedFiscalYear]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pnl/categories`, config);
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!selectedFiscalYear) return;
    try {
      const response = await axios.get(`${API}/pnl/entries?fiscal_year=${selectedFiscalYear}`, config);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load entries');
    }
  }, [selectedFiscalYear]);

  const fetchSummary = useCallback(async () => {
    if (!selectedFiscalYear) return;
    try {
      const response = await axios.get(`${API}/pnl/summary?fiscal_year=${selectedFiscalYear}`, config);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [selectedFiscalYear]);

  const fetchCategoryBreakdown = useCallback(async () => {
    if (!selectedFiscalYear) return;
    try {
      const response = await axios.get(`${API}/pnl/category-breakdown?fiscal_year=${selectedFiscalYear}`, config);
      setCategoryBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching breakdown:', error);
    }
  }, [selectedFiscalYear]);

  const fetchYoyComparison = useCallback(async () => {
    if (!selectedFiscalYear) return;
    try {
      const response = await axios.get(`${API}/pnl/yoy-comparison?fiscal_year=${selectedFiscalYear}`, config);
      setYoyData(response.data);
    } catch (error) {
      console.error('Error fetching YoY data:', error);
    }
  }, [selectedFiscalYear]);

  const fetchQuarterlySummary = useCallback(async () => {
    if (!selectedFiscalYear) return;
    try {
      const response = await axios.get(`${API}/pnl/quarterly-summary?fiscal_year=${selectedFiscalYear}`, config);
      setQuarterlyData(response.data);
    } catch (error) {
      console.error('Error fetching quarterly data:', error);
    }
  }, [selectedFiscalYear]);

  const fetchMonthDetail = async (month) => {
    setMonthDetailLoading(true);
    try {
      const response = await axios.get(`${API}/pnl/monthly-detail/${month}?fiscal_year=${selectedFiscalYear}`, config);
      setSelectedMonthDetail(response.data);
      setShowMonthDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load month details');
    } finally {
      setMonthDetailLoading(false);
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchFiscalYears(),
      fetchCategories(),
    ]);
    setLoading(false);
  }, [fetchFiscalYears, fetchCategories]);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (selectedFiscalYear) {
      fetchEntries();
      fetchSummary();
      fetchCategoryBreakdown();
      fetchYoyComparison();
      fetchQuarterlySummary();
    }
  }, [selectedFiscalYear, fetchEntries, fetchSummary, fetchCategoryBreakdown, fetchYoyComparison, fetchQuarterlySummary]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/pnl/categories/${editingCategory.id}`, categoryForm, config);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/pnl/categories`, categoryForm, config);
        toast.success('Category created successfully');
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', type: 'cost', description: '' });
      fetchCategories();
      fetchCategoryBreakdown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? All associated entries will be preserved but hidden.')) return;
    try {
      await axios.delete(`${API}/pnl/categories/${categoryId}`, config);
      toast.success('Category deleted successfully');
      fetchCategories();
      fetchCategoryBreakdown();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, type: category.type, description: category.description || '' });
    setShowCategoryDialog(true);
  };

  const handleCreateFiscalYear = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pnl/fiscal-years`, newFYForm, config);
      toast.success(`Fiscal Year ${newFYForm.fiscal_year} created successfully`);
      setShowNewFYDialog(false);
      setNewFYForm({ fiscal_year: '' });
      fetchFiscalYears();
      setSelectedFiscalYear(newFYForm.fiscal_year);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fiscal year');
    }
  };

  const handleBulkSave = async (categoryId, monthlyData) => {
    try {
      const entriesArray = Object.entries(monthlyData).map(([month, data]) => ({
        month: parseInt(month),
        projected: parseFloat(data.projected) || 0,
        actual: parseFloat(data.actual) || 0,
        remark: data.remark || ''
      }));
      
      await axios.post(`${API}/pnl/entries/bulk`, {
        category_id: categoryId,
        fiscal_year: selectedFiscalYear,
        entries: entriesArray
      }, config);
      
      toast.success('All entries saved');
      fetchEntries();
      fetchSummary();
      fetchCategoryBreakdown();
      fetchQuarterlySummary();
    } catch (error) {
      toast.error('Failed to save entries');
    }
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Prepare chart data
  const chartData = summary?.monthly_data?.map(d => ({
    name: FISCAL_MONTHS.find(m => m.month === d.month)?.name || d.month_name,
    revenue_projected: d.revenue_projected,
    revenue_actual: d.revenue_actual,
    cost_projected: d.cost_projected,
    cost_actual: d.cost_actual,
    profit_projected: d.profit_projected,
    profit_actual: d.profit_actual,
    margin_projected: d.margin_projected,
    margin_actual: d.margin_actual
  })) || [];

  // Prepare quarterly chart data
  const quarterlyChartData = quarterlyData?.quarterly_data?.map(q => ({
    name: q.quarter,
    revenue_actual: q.revenue_actual,
    cost_actual: q.cost_actual,
    profit_actual: q.profit_actual
  })) || [];

  // Get cost categories for dropdown
  const costCategories = categories.filter(c => c.type === 'cost');

  // Prepare cost category chart data based on selected category
  const getCostCategoryChartData = () => {
    if (!selectedCostCategory || !categoryBreakdown) return [];
    
    const category = categoryBreakdown.cost_categories?.find(c => c.id === selectedCostCategory);
    if (!category) return [];
    
    return FISCAL_MONTHS.map(m => ({
      name: m.name,
      fullName: m.fullName,
      projected: category.monthly[m.month]?.projected || 0,
      actual: category.monthly[m.month]?.actual || 0,
      variance: (category.monthly[m.month]?.actual || 0) - (category.monthly[m.month]?.projected || 0)
    }));
  };

  const costCategoryChartData = getCostCategoryChartData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pnl-portal">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <IndianRupee className="w-6 h-6 text-green-600" />
            P&L Portal
          </h2>
          <p className="text-gray-600">Profit & Loss Management - FY {selectedFiscalYear}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedFiscalYear} onValueChange={setSelectedFiscalYear}>
            <SelectTrigger className="w-[140px]" data-testid="fiscal-year-select">
              <SelectValue placeholder="Select FY" />
            </SelectTrigger>
            <SelectContent>
              {fiscalYears.map(fy => (
                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Add New Fiscal Year Button */}
          <Dialog open={showNewFYDialog} onOpenChange={setShowNewFYDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-fy-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add FY
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Fiscal Year</DialogTitle>
                <DialogDescription>Create a new fiscal year to start entering P&L data</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateFiscalYear} className="space-y-4">
                <div>
                  <Label>Fiscal Year *</Label>
                  <Input
                    value={newFYForm.fiscal_year}
                    onChange={(e) => setNewFYForm({ fiscal_year: e.target.value })}
                    placeholder="e.g., 2026-27"
                    required
                    pattern="\d{4}-\d{2}"
                    title="Format: YYYY-YY (e.g., 2026-27)"
                    data-testid="new-fy-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: YYYY-YY (e.g., 2026-27 for April 2026 - March 2027)</p>
                </div>
                <Button type="submit" className="w-full" data-testid="create-fy-btn">
                  Create Fiscal Year
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" onClick={() => { fetchSummary(); fetchCategoryBreakdown(); fetchQuarterlySummary(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="data-entry" data-testid="tab-data-entry">
            <Edit className="w-4 h-4 mr-2" />
            Data Entry
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            <PieChart className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200" data-testid="revenue-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(summary?.yearly_totals?.revenue_actual)}</p>
                    <p className="text-xs text-green-600 mt-1">
                      Projected: {formatCurrency(summary?.yearly_totals?.revenue_projected)}
                    </p>
                  </div>
                  <div className="p-2 bg-green-200 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200" data-testid="cost-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-red-600">Total Cost</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(summary?.yearly_totals?.cost_actual)}</p>
                    <p className="text-xs text-red-600 mt-1">
                      Projected: {formatCurrency(summary?.yearly_totals?.cost_projected)}
                    </p>
                  </div>
                  <div className="p-2 bg-red-200 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-red-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`} data-testid="profit-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-sm font-medium ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      Net {(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'Profit' : 'Loss'}
                    </p>
                    <p className={`text-2xl font-bold ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                      {formatCurrency(Math.abs(summary?.yearly_totals?.profit_actual || 0))}
                    </p>
                    <p className={`text-xs mt-1 ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      Projected: {formatCurrency(summary?.yearly_totals?.profit_projected)}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'bg-blue-200' : 'bg-orange-200'}`}>
                    <DollarSign className={`w-5 h-5 ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200" data-testid="margin-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Actual Margin</p>
                    <p className="text-2xl font-bold text-purple-700">{summary?.yearly_totals?.margin_actual || 0}%</p>
                    <p className="text-xs text-purple-600 mt-1">
                      Projected: {summary?.yearly_totals?.margin_projected || 0}%
                    </p>
                  </div>
                  <div className="p-2 bg-purple-200 rounded-lg">
                    <PieChart className="w-5 h-5 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts - Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue vs Cost Chart */}
            <Card data-testid="revenue-cost-chart">
              <CardHeader>
                <CardTitle className="text-lg">Monthly Revenue vs Cost</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatFullCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue_actual" name="Revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cost_actual" name="Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Quarterly Revenue vs Cost Chart - NEW */}
            <Card data-testid="quarterly-chart">
              <CardHeader>
                <CardTitle className="text-lg">Quarterly Revenue vs Cost</CardTitle>
                <CardDescription>Q1: Apr-Jun, Q2: Jul-Sep, Q3: Oct-Dec, Q4: Jan-Mar</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={quarterlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatFullCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue_actual" name="Revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cost_actual" name="Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit_actual" name="Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts - Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Profit Trend Chart */}
            <Card data-testid="profit-trend-chart">
              <CardHeader>
                <CardTitle className="text-lg">Profit/Loss Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatFullCurrency(value)} />
                    <Legend />
                    <Area type="monotone" dataKey="profit_projected" name="Projected Profit" fill="#93c5fd" stroke="#3b82f6" fillOpacity={0.3} />
                    <Line type="monotone" dataKey="profit_actual" name="Actual Profit" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Margin Trend */}
            <Card data-testid="margin-trend-chart">
              <CardHeader>
                <CardTitle className="text-lg">Margin Trend (%)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} domain={['auto', 'auto']} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="margin_projected" name="Projected Margin" stroke="#a855f7" strokeWidth={2} strokeDasharray="5 5" />
                    <Line type="monotone" dataKey="margin_actual" name="Actual Margin" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Cost Item Analysis Chart */}
          <Card data-testid="cost-item-chart">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Cost Item Analysis
                  </CardTitle>
                  <CardDescription>Select a cost item to view projected vs actual month-wise</CardDescription>
                </div>
                <Select value={selectedCostCategory} onValueChange={setSelectedCostCategory}>
                  <SelectTrigger className="w-[250px]" data-testid="cost-category-select">
                    <SelectValue placeholder="Select Cost Item" />
                  </SelectTrigger>
                  <SelectContent>
                    {costCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedCostCategory ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingDown className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a cost item from the dropdown to view the analysis</p>
                </div>
              ) : costCategoryChartData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No data available for the selected cost item</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={costCategoryChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip 
                      formatter={(value, name) => [formatFullCurrency(value), name]}
                      labelFormatter={(label) => {
                        const month = costCategoryChartData.find(d => d.name === label);
                        return month?.fullName || label;
                      }}
                    />
                    <Legend />
                    <Bar dataKey="projected" name="Projected Cost" fill="#fca5a5" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" name="Actual Cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="variance" name="Variance" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              {selectedCostCategory && costCategoryChartData.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Projected</p>
                    <p className="text-lg font-bold text-red-600">
                      {formatCurrency(costCategoryChartData.reduce((sum, d) => sum + d.projected, 0))}
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <p className="text-sm text-gray-600">Total Actual</p>
                    <p className="text-lg font-bold text-red-700">
                      {formatCurrency(costCategoryChartData.reduce((sum, d) => sum + d.actual, 0))}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${costCategoryChartData.reduce((sum, d) => sum + d.variance, 0) <= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                    <p className="text-sm text-gray-600">Total Variance</p>
                    <p className={`text-lg font-bold ${costCategoryChartData.reduce((sum, d) => sum + d.variance, 0) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {costCategoryChartData.reduce((sum, d) => sum + d.variance, 0) >= 0 ? '+' : ''}
                      {formatCurrency(costCategoryChartData.reduce((sum, d) => sum + d.variance, 0))}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* YoY Comparison */}
          <Card data-testid="yoy-comparison">
            <CardHeader>
              <CardTitle className="text-lg">Year-over-Year Comparison</CardTitle>
              <CardDescription>
                {yoyData?.previous_year ? `FY ${selectedFiscalYear} vs Previous Year` : 'No previous year data available'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {yoyData?.previous_year ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Revenue Growth</p>
                      <p className={`text-xl font-bold ${yoyData.comparison.revenue_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {yoyData.comparison.revenue_growth >= 0 ? '+' : ''}{yoyData.comparison.revenue_growth}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-gray-600">Cost Growth</p>
                      <p className={`text-xl font-bold ${yoyData.comparison.cost_growth <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {yoyData.comparison.cost_growth >= 0 ? '+' : ''}{yoyData.comparison.cost_growth}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">Profit Growth</p>
                      <p className={`text-xl font-bold ${yoyData.comparison.profit_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {yoyData.comparison.profit_growth >= 0 ? '+' : ''}{yoyData.comparison.profit_growth}%
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Add data for previous fiscal year to see comparison</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Summary Table with View Detail */}
          <Card data-testid="monthly-summary-table">
            <CardHeader>
              <CardTitle className="text-lg">Monthly Summary - FY {selectedFiscalYear}</CardTitle>
              <CardDescription>Click the eye icon to view detailed breakdown for each month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Month</TableHead>
                      <TableHead className="text-right font-semibold text-green-600">Revenue (P)</TableHead>
                      <TableHead className="text-right font-semibold text-green-700">Revenue (A)</TableHead>
                      <TableHead className="text-right font-semibold text-red-600">Cost (P)</TableHead>
                      <TableHead className="text-right font-semibold text-red-700">Cost (A)</TableHead>
                      <TableHead className="text-right font-semibold text-blue-600">Profit (P)</TableHead>
                      <TableHead className="text-right font-semibold text-blue-700">Profit (A)</TableHead>
                      <TableHead className="text-right font-semibold text-purple-600">Margin (A)</TableHead>
                      <TableHead className="text-center font-semibold">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary?.monthly_data?.map((data) => (
                      <TableRow key={data.month} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{data.month_name}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(data.revenue_projected)}</TableCell>
                        <TableCell className="text-right font-medium text-green-700">{formatCurrency(data.revenue_actual)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(data.cost_projected)}</TableCell>
                        <TableCell className="text-right font-medium text-red-700">{formatCurrency(data.cost_actual)}</TableCell>
                        <TableCell className="text-right text-blue-600">{formatCurrency(data.profit_projected)}</TableCell>
                        <TableCell className={`text-right font-medium ${data.profit_actual >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                          {formatCurrency(data.profit_actual)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-purple-600">{data.margin_actual}%</TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => fetchMonthDetail(data.month)}
                            disabled={monthDetailLoading}
                            data-testid={`view-detail-${data.month}`}
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Yearly Total Row */}
                    <TableRow className="bg-gray-100 font-bold border-t-2">
                      <TableCell>Total (FY {selectedFiscalYear})</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(summary?.yearly_totals?.revenue_projected)}</TableCell>
                      <TableCell className="text-right text-green-700">{formatCurrency(summary?.yearly_totals?.revenue_actual)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(summary?.yearly_totals?.cost_projected)}</TableCell>
                      <TableCell className="text-right text-red-700">{formatCurrency(summary?.yearly_totals?.cost_actual)}</TableCell>
                      <TableCell className="text-right text-blue-600">{formatCurrency(summary?.yearly_totals?.profit_projected)}</TableCell>
                      <TableCell className={`text-right ${(summary?.yearly_totals?.profit_actual || 0) >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                        {formatCurrency(summary?.yearly_totals?.profit_actual)}
                      </TableCell>
                      <TableCell className="text-right text-purple-600">{summary?.yearly_totals?.margin_actual}%</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-gray-500 mt-2">P = Projected, A = Actual</p>
            </CardContent>
          </Card>

          {/* Month Detail Dialog */}
          <Dialog open={showMonthDetailDialog} onOpenChange={setShowMonthDetailDialog}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {selectedMonthDetail?.month_name} Detail - FY {selectedMonthDetail?.fiscal_year}
                </DialogTitle>
                <DialogDescription>
                  Detailed breakdown of revenue and cost items for {selectedMonthDetail?.month_name}
                </DialogDescription>
              </DialogHeader>
              
              {selectedMonthDetail && (
                <div className="space-y-6">
                  {/* Revenue Items */}
                  <div>
                    <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Revenue Items
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Projected</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Variance</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedMonthDetail.revenue_items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.category_name}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.projected)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.actual)}</TableCell>
                            <TableCell className={`text-right ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.variance >= 0 ? '+' : ''}{formatCurrency(item.variance)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">{item.remark || '-'}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-green-100 font-bold">
                          <TableCell>Total Revenue</TableCell>
                          <TableCell className="text-right">{formatCurrency(selectedMonthDetail.totals.revenue_projected)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(selectedMonthDetail.totals.revenue_actual)}</TableCell>
                          <TableCell className={`text-right ${selectedMonthDetail.totals.revenue_actual - selectedMonthDetail.totals.revenue_projected >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(selectedMonthDetail.totals.revenue_actual - selectedMonthDetail.totals.revenue_projected)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Cost Items */}
                  <div>
                    <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Cost Items
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-50">
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Projected</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Variance</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedMonthDetail.cost_items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.category_name}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.projected)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.actual)}</TableCell>
                            <TableCell className={`text-right ${item.variance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.variance >= 0 ? '+' : ''}{formatCurrency(item.variance)}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">{item.remark || '-'}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-red-100 font-bold">
                          <TableCell>Total Cost</TableCell>
                          <TableCell className="text-right">{formatCurrency(selectedMonthDetail.totals.cost_projected)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(selectedMonthDetail.totals.cost_actual)}</TableCell>
                          <TableCell className={`text-right ${selectedMonthDetail.totals.cost_actual - selectedMonthDetail.totals.cost_projected <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(selectedMonthDetail.totals.cost_actual - selectedMonthDetail.totals.cost_projected)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Summary */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-700 mb-3">Month Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Net Profit/Loss (Projected)</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedMonthDetail.totals.profit_projected)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Net Profit/Loss (Actual)</p>
                        <p className={`text-xl font-bold ${selectedMonthDetail.totals.profit_actual >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                          {formatCurrency(selectedMonthDetail.totals.profit_actual)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Margin (Projected)</p>
                        <p className="text-xl font-bold text-purple-700">{selectedMonthDetail.totals.margin_projected}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Margin (Actual)</p>
                        <p className="text-xl font-bold text-purple-700">{selectedMonthDetail.totals.margin_actual}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Data Entry Tab */}
        <TabsContent value="data-entry" className="space-y-6">
          <Card data-testid="data-entry-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                Data Entry - FY {selectedFiscalYear}
              </CardTitle>
              <CardDescription>
                Click on a category to expand and enter monthly projected, actual values, and remarks
              </CardDescription>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <PieChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-4">No categories found. Create categories first to enter data.</p>
                  <Button onClick={() => setActiveTab('categories')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Go to Categories
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Revenue Categories */}
                  <div>
                    <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Revenue Categories
                    </h3>
                    {categories.filter(c => c.type === 'revenue').length === 0 ? (
                      <p className="text-gray-500 text-sm ml-7">No revenue categories. Add some from the Categories tab.</p>
                    ) : (
                      categories.filter(c => c.type === 'revenue').map(category => (
                        <CategoryDataEntry
                          key={category.id}
                          category={category}
                          entries={entries}
                          fiscalYear={selectedFiscalYear}
                          expanded={expandedCategories[category.id]}
                          onToggle={() => toggleCategoryExpand(category.id)}
                          onSave={handleBulkSave}
                          colorClass="green"
                        />
                      ))
                    )}
                  </div>

                  {/* Cost Categories */}
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Cost Categories
                    </h3>
                    {categories.filter(c => c.type === 'cost').length === 0 ? (
                      <p className="text-gray-500 text-sm ml-7">No cost categories. Add some from the Categories tab.</p>
                    ) : (
                      categories.filter(c => c.type === 'cost').map(category => (
                        <CategoryDataEntry
                          key={category.id}
                          category={category}
                          entries={entries}
                          fiscalYear={selectedFiscalYear}
                          expanded={expandedCategories[category.id]}
                          onToggle={() => toggleCategoryExpand(category.id)}
                          onSave={handleBulkSave}
                          colorClass="red"
                        />
                      ))
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-6">
          <Card data-testid="categories-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Manage Categories
                </CardTitle>
                <CardDescription>Add, edit or remove cost and revenue categories</CardDescription>
              </div>
              <Dialog open={showCategoryDialog} onOpenChange={(open) => {
                setShowCategoryDialog(open);
                if (!open) {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', type: 'cost', description: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? 'Edit Category' : 'Create New Category'}</DialogTitle>
                    <DialogDescription>
                      {editingCategory ? 'Update the category details' : 'Add a new cost or revenue category'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4" data-testid="category-form">
                    <div>
                      <Label>Category Name *</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        required
                        placeholder="e.g., Salaries, Product Sales"
                        data-testid="category-name-input"
                      />
                    </div>
                    <div>
                      <Label>Type *</Label>
                      <Select 
                        value={categoryForm.type} 
                        onValueChange={(v) => setCategoryForm({ ...categoryForm, type: v })}
                        disabled={!!editingCategory}
                      >
                        <SelectTrigger data-testid="category-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cost">Cost (Expense)</SelectItem>
                          <SelectItem value="revenue">Revenue (Income)</SelectItem>
                        </SelectContent>
                      </Select>
                      {editingCategory && (
                        <p className="text-xs text-gray-500 mt-1">Category type cannot be changed after creation</p>
                      )}
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        placeholder="Brief description of this category"
                        data-testid="category-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="save-category-btn">
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Revenue Categories */}
                <div>
                  <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Revenue Categories ({categories.filter(c => c.type === 'revenue').length})
                  </h3>
                  <div className="space-y-2">
                    {categories.filter(c => c.type === 'revenue').length === 0 ? (
                      <p className="text-gray-500 text-sm">No revenue categories yet</p>
                    ) : (
                      categories.filter(c => c.type === 'revenue').map(cat => (
                        <div 
                          key={cat.id} 
                          className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                          data-testid={`category-${cat.id}`}
                        >
                          <div>
                            <p className="font-medium text-green-800">{cat.name}</p>
                            {cat.description && <p className="text-xs text-green-600">{cat.description}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditCategory(cat)} data-testid={`edit-cat-${cat.id}`}>
                              <Edit className="w-4 h-4 text-green-700" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Cost Categories */}
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    Cost Categories ({categories.filter(c => c.type === 'cost').length})
                  </h3>
                  <div className="space-y-2">
                    {categories.filter(c => c.type === 'cost').length === 0 ? (
                      <p className="text-gray-500 text-sm">No cost categories yet</p>
                    ) : (
                      categories.filter(c => c.type === 'cost').map(cat => (
                        <div 
                          key={cat.id} 
                          className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                          data-testid={`category-${cat.id}`}
                        >
                          <div>
                            <p className="font-medium text-red-800">{cat.name}</p>
                            {cat.description && <p className="text-xs text-red-600">{cat.description}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditCategory(cat)} data-testid={`edit-cat-${cat.id}`}>
                              <Edit className="w-4 h-4 text-red-700" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Category Data Entry Component with Remark field
const CategoryDataEntry = ({ category, entries, fiscalYear, expanded, onToggle, onSave, colorClass }) => {
  const [monthlyData, setMonthlyData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Initialize monthly data from existing entries
    const data = {};
    FISCAL_MONTHS.forEach(m => {
      const entry = entries.find(e => e.category_id === category.id && e.month === m.month);
      data[m.month] = {
        projected: entry?.projected || 0,
        actual: entry?.actual || 0,
        remark: entry?.remark || ''
      };
    });
    setMonthlyData(data);
    setHasChanges(false);
  }, [entries, category.id]);

  const handleChange = (month, field, value) => {
    setMonthlyData(prev => ({
      ...prev,
      [month]: {
        ...prev[month],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(category.id, monthlyData);
    setHasChanges(false);
  };

  const totalProjected = Object.values(monthlyData).reduce((sum, d) => sum + (parseFloat(d.projected) || 0), 0);
  const totalActual = Object.values(monthlyData).reduce((sum, d) => sum + (parseFloat(d.actual) || 0), 0);

  return (
    <div className={`border rounded-lg mb-2 ${colorClass === 'green' ? 'border-green-200' : 'border-red-200'}`}>
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer ${colorClass === 'green' ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100'}`}
        onClick={onToggle}
        data-testid={`category-expand-${category.id}`}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-medium">{category.name}</span>
          {category.description && (
            <span className="text-xs text-gray-500">({category.description})</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span>Projected: {formatCurrency(totalProjected)}</span>
          <span>Actual: {formatCurrency(totalActual)}</span>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Month</TableHead>
                  <TableHead className="text-center">Projected (₹)</TableHead>
                  <TableHead className="text-center">Actual (₹)</TableHead>
                  <TableHead className="text-center w-24">Variance</TableHead>
                  <TableHead className="text-center">Remark</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FISCAL_MONTHS.map(m => {
                  const variance = (parseFloat(monthlyData[m.month]?.actual) || 0) - (parseFloat(monthlyData[m.month]?.projected) || 0);
                  // For cost: negative variance (under budget) is good (green), positive (over budget) is bad (red)
                  // For revenue: positive variance (over target) is good (green), negative (under target) is bad (red)
                  const isGoodVariance = colorClass === 'green' ? variance >= 0 : variance <= 0;
                  return (
                    <TableRow key={m.month}>
                      <TableCell className="font-medium">{m.fullName}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={monthlyData[m.month]?.projected || ''}
                          onChange={(e) => handleChange(m.month, 'projected', e.target.value)}
                          placeholder="0"
                          className="text-center"
                          data-testid={`projected-${category.id}-${m.month}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={monthlyData[m.month]?.actual || ''}
                          onChange={(e) => handleChange(m.month, 'actual', e.target.value)}
                          placeholder="0"
                          className="text-center"
                          data-testid={`actual-${category.id}-${m.month}`}
                        />
                      </TableCell>
                      <TableCell className={`text-center font-medium ${isGoodVariance ? 'text-green-600' : 'text-red-600'}`}>
                        {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          value={monthlyData[m.month]?.remark || ''}
                          onChange={(e) => handleChange(m.month, 'remark', e.target.value)}
                          placeholder="Add remark..."
                          className="text-sm"
                          data-testid={`remark-${category.id}-${m.month}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow className="bg-gray-50 font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-center">{formatCurrency(totalProjected)}</TableCell>
                  <TableCell className="text-center">{formatCurrency(totalActual)}</TableCell>
                  <TableCell className={`text-center ${colorClass === 'green' ? (totalActual - totalProjected >= 0 ? 'text-green-600' : 'text-red-600') : (totalActual - totalProjected <= 0 ? 'text-green-600' : 'text-red-600')}`}>
                    {totalActual - totalProjected >= 0 ? '+' : ''}{formatCurrency(totalActual - totalProjected)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges}
              className={`${colorClass === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-testid={`save-entries-${category.id}`}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Entries
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PnLPortal;
